import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useVirtualizer } from '@tanstack/react-virtual';
import Fund from '@/models/Fund';
import { useGetAuthApi } from '@/hooks/UseApiCalls';
import Dropdown from '@/components/ui/Dropdown';
import Button from '@/components/ui/Button';
import SearchInput from '@/components/ui/SearchInput/SearchInput';
import chevron from '@/assets/icons/chevron.svg';
import icFilterExpander from '@/assets/icons/filterExpander.svg';
import filterStyles from '@/components/filters/OptionsRangeFilter/OptionsRangeFilter.module.css';
import fundStyles from '@/components/FundSelect/fundSelect.module.css';

interface FundFilterProps {
  selectedFundId: number | null;
  onFundChange: (fundId: number | null) => void;
  /**
   * Endpoint the list is read from. Defaults to every fund the accounting system knows; screens
   * that act on a fund's own records should point this at a list of funds registered here, so a
   * fund that cannot be acted on is never offered.
   */
  fundsApiUrl?: string;
}

const FundFilter = ({ selectedFundId, onFundChange, fundsApiUrl = 'funds' }: FundFilterProps) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null) as React.RefObject<HTMLInputElement>;
  const listContainerRef = useRef<HTMLDivElement>(null);

  const { data: funds, isLoading } = useGetAuthApi<Fund[]>(fundsApiUrl);

  const filteredFunds = (funds ?? []).filter((f) =>
    `${f.id} ${f.name}`.toLowerCase().includes(searchValue.toLowerCase())
  );

  const virtualizer = useVirtualizer({
    count: filteredFunds.length,
    getScrollElement: () => listContainerRef.current,
    estimateSize: () => 40,
    overscan: 5,
  });

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  const handleSetOpen = (open: boolean) => {
    setIsOpen(open);
    if (!open) setSearchValue('');
  };

  const handleSelect = (fund: Fund) => {
    onFundChange(fund.id);
    setIsOpen(false);
    setSearchValue('');
  };

  const handleClear = () => {
    onFundChange(null);
    setIsOpen(false);
    setSearchValue('');
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && filteredFunds.length > 0) {
      handleSelect(filteredFunds[0]);
    }
  };

  const selectedFund = selectedFundId != null ? (funds ?? []).find((f) => f.id === selectedFundId) : null;
  const hasValue = selectedFundId != null;

  const triggerLabel = selectedFund
    ? `${selectedFund.id} | ${selectedFund.name}`
    : t('fund');

  return (
    <Dropdown
      trigger={
        <Button
          className={`
            ${filterStyles.filterButton}
            ${isOpen ? filterStyles.filterButtonDropdownOpen : ''}
            ${hasValue ? filterStyles.filterButtonHasValue : ''}
          `}
        >
          <span className="body-md-regular text-(--font-primary)">{triggerLabel}</span>
          <img src={icFilterExpander} alt="expander" />
        </Button>
      }
      contentAlign="start"
      isOpen={isOpen}
      setIsOpen={handleSetOpen}
    >
      <div className={fundStyles['select-menu']}>
        <SearchInput
          value={searchValue}
          onChange={setSearchValue}
          placeholder={t('searchFund')}
          className={fundStyles['search-container']}
          inputRef={searchInputRef}
          onKeyDown={handleKeyDown}
        />
        <div className={fundStyles['options-container']}>
          {hasValue && (
            <button className={fundStyles.option} onClick={handleClear}>
              <span className="body-md-regular">{t('allFunds')}</span>
            </button>
          )}
          {isLoading ? (
            <p className={`${fundStyles.option} body-sm-regular`}>{t('loadingFunds')}...</p>
          ) : filteredFunds.length === 0 ? (
            <p className={`${fundStyles.option} body-sm-regular`}>{t('noFundsFound')}</p>
          ) : (
            <div
              ref={listContainerRef}
              className={fundStyles['virtual-container']}
              style={{ height: Math.min(filteredFunds.length * 40, 220) }}
            >
              <div
                className={fundStyles['virtual-content']}
                style={{ height: `${virtualizer.getTotalSize()}px` }}
              >
                {virtualizer.getVirtualItems().map((virtualRow) => {
                  const fund = filteredFunds[virtualRow.index];
                  return (
                    <div
                      key={virtualRow.key}
                      ref={virtualizer.measureElement}
                      data-index={virtualRow.index}
                      className={fundStyles['virtual-item']}
                      style={{ transform: `translateY(${virtualRow.start}px)` }}
                    >
                      <button
                        className={`${fundStyles.option} ${fund.id === selectedFundId ? 'bg-(--surface-secondary-lowest)' : ''}`}
                        onClick={() => handleSelect(fund)}
                      >
                        <span className="body-md-semibold">{fund.id}</span>
                        <div className={fundStyles.divider} />
                        <span className="body-sm-regular">{fund.name}</span>
                        <span className="ms-auto">
                          <img src={chevron} alt="" className={fundStyles.chevron} />
                        </span>
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </Dropdown>
  );
};

export default FundFilter;
